import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { ColumnDef, DataTable } from '../../Components/Table/DataTable';
import { MachineListStatusIndicator } from '../../Components/Vending/MachineStatusChip';
import { Header } from '../../Components/Header/Header';
import vendingApiClient from '../../Services/VendingApi';
import { formatRelativeTime } from '../../Services/FormatRelativeTime';
import { formatUTC } from '../../Services/FormatUtc';
import { PageContainer } from '../../Styles/AppStyles';
import { COLORS } from '../../Styles/Colors';
import {
  VendingMachineListResponse,
  VendingMachineSummary,
  VendingMappedCompany,
  VendingMappedLocation,
} from '../../Types/vending';

const SEARCH_DEBOUNCE_MS = 400;

const locationDisplay = (row: VendingMachineSummary) => {
  if (row.location_name) return row.location_name;
  const parts = [row.city, row.state].filter(Boolean);
  if (parts.length) return parts.join(', ');
  return 'Unassigned';
};

const customerDisplay = (row: VendingMachineSummary) => {
  if (row.customer_name) return row.customer_name;
  if (row.location_map_status === 'mapped') return row.customer_label || 'Mapped';
  return 'Unassigned';
};

export const VendingMachinesPage = () => {
  const navigate = useNavigate();
  const [machines, setMachines] = useState<VendingMachineSummary[]>([]);
  const [companies, setCompanies] = useState<VendingMappedCompany[]>([]);
  const [locations, setLocations] = useState<VendingMappedLocation[]>([]);
  const [asOf, setAsOf] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [connectionFilter, setConnectionFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [machineTypeFilter, setMachineTypeFilter] = useState('');
  const [alertSeverityFilter, setAlertSeverityFilter] = useState('');
  const [needsRestockFilter, setNeedsRestockFilter] = useState(false);
  const [alertsFilter, setAlertsFilter] = useState(false);
  const [outOfStockFilter, setOutOfStockFilter] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setLocationFilter('');
  }, [companyFilter]);

  const hasActiveFilters = Boolean(
    debouncedSearch
    || statusFilter
    || connectionFilter
    || companyFilter
    || locationFilter
    || machineTypeFilter
    || alertSeverityFilter
    || needsRestockFilter
    || alertsFilter
    || outOfStockFilter,
  );

  const loadMachines = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({ limit: '200' });
      if (debouncedSearch) params.set('q', debouncedSearch);
      if (statusFilter) params.set('status', statusFilter);
      if (connectionFilter) params.set('connection_status', connectionFilter);
      if (companyFilter) params.set('cid', companyFilter);
      if (locationFilter) params.set('ovid', locationFilter);
      if (machineTypeFilter) params.set('machine_type', machineTypeFilter);
      if (alertSeverityFilter) params.set('alert_severity', alertSeverityFilter);
      if (needsRestockFilter) params.set('needs_restock', 'true');
      if (alertsFilter) params.set('has_alerts', 'true');
      if (outOfStockFilter) params.set('has_empty', 'true');

      const response = await vendingApiClient(`/machines?${params.toString()}`);
      const json: VendingMachineListResponse = await response.json();
      setMachines(json.data);
      setCompanies(json.meta?.companies ?? []);
      setLocations(json.meta?.locations ?? []);
      setAsOf(json.meta?.as_of ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load machines');
      setMachines([]);
    } finally {
      setLoading(false);
    }
  }, [
    debouncedSearch,
    statusFilter,
    connectionFilter,
    companyFilter,
    locationFilter,
    machineTypeFilter,
    alertSeverityFilter,
    needsRestockFilter,
    alertsFilter,
    outOfStockFilter,
  ]);

  useEffect(() => {
    loadMachines();
  }, [loadMachines]);

  const clearFilters = useCallback(() => {
    setSearchInput('');
    setDebouncedSearch('');
    setStatusFilter('');
    setConnectionFilter('');
    setCompanyFilter('');
    setLocationFilter('');
    setMachineTypeFilter('');
    setAlertSeverityFilter('');
    setNeedsRestockFilter(false);
    setAlertsFilter(false);
    setOutOfStockFilter(false);
  }, []);

  const columns: ColumnDef<VendingMachineSummary>[] = useMemo(() => [
    {
      field: 'connection_status',
      headerName: 'Status',
      width: 110,
      renderCell: (params) => (
        <MachineListStatusIndicator
          connection_status={params.row.connection_status}
          status={params.row.status}
          open_alerts_count={params.row.open_alerts_count}
          empty_slots={params.row.empty_slots}
          low_slots={params.row.low_slots}
        />
      ),
    },
    {
      field: 'machine_name',
      headerName: 'Machine',
      width: 180,
      searchable: true,
      valueGetter: (_v, row) => row.machine_name || `VM-${row.provider_machine_id}`,
    },
    {
      field: 'customer_name',
      headerName: 'Customer',
      width: 200,
      searchable: true,
      valueGetter: (_v, row) => customerDisplay(row),
    },
    {
      field: 'location_name',
      headerName: 'Location',
      width: 200,
      searchable: true,
      valueGetter: (_v, row) => locationDisplay(row),
    },
    {
      field: 'inventory_health_pct',
      headerName: 'Inventory Health',
      width: 130,
      valueGetter: (_v, row) => (
        row.inventory_health_pct != null ? `${row.inventory_health_pct}%` : '—'
      ),
    },
    {
      field: 'empty_slots',
      headerName: 'Products Empty',
      width: 120,
      type: 'number',
      renderCell: (params) => (
        <Typography
          variant="body2"
          fontWeight={params.value > 0 ? 700 : 400}
          color={params.value > 0 ? 'error.main' : 'text.primary'}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'open_alerts_count',
      headerName: 'Active Alerts',
      width: 110,
      type: 'number',
      renderCell: (params) => (
        <Typography
          variant="body2"
          fontWeight={params.value > 0 ? 700 : 400}
          color={params.value > 0 ? 'warning.main' : 'text.primary'}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'last_communication',
      headerName: 'Last Communication',
      width: 140,
      valueGetter: (_v, row) => formatRelativeTime(row.last_communication),
    },
    {
      field: 'last_refill_at',
      headerName: 'Last Restock',
      width: 130,
      valueGetter: (_v, row) => formatRelativeTime(row.last_refill_at),
    },
  ], []);

  const rows = useMemo(
    () => machines.map((m) => ({
      ...m,
      id: m.id,
      machine_name: m.machine_name || `VM-${m.provider_machine_id}`,
    })),
    [machines],
  );

  return (
    <PageContainer>
      <Header
        title="Machines"
        subtitle={asOf ? `Data as of ${formatUTC(asOf)} UTC` : undefined}
        count={rows.length}
      >
        <Button variant="outlined" onClick={loadMachines} disabled={loading}>
          Refresh
        </Button>
      </Header>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            pt: { xs: 2, md: 3 },
            pb: 2,
            flexShrink: 0,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'center',
          }}
        >
          <TextField
            size="small"
            label="Search"
            placeholder="Search machine, customer or location..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ minWidth: 280, flex: { xs: '1 1 100%', md: '1 1 320px' } }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="healthy">Healthy</MenuItem>
              <MenuItem value="attention">Attention</MenuItem>
              <MenuItem value="action_required">Action Required</MenuItem>
              <MenuItem value="critical">Critical</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Connection</InputLabel>
            <Select
              label="Connection"
              value={connectionFilter}
              onChange={(e) => setConnectionFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="online">Online</MenuItem>
              <MenuItem value="delayed">Delayed</MenuItem>
              <MenuItem value="offline">Offline</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Customer</InputLabel>
            <Select
              label="Customer"
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
            >
              <MenuItem value="">All companies</MenuItem>
              {companies.map((company) => (
                <MenuItem key={company.cid} value={String(company.cid)}>
                  {company.customer_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }} disabled={!companyFilter}>
            <InputLabel>Location</InputLabel>
            <Select
              label="Location"
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
            >
              <MenuItem value="">All locations</MenuItem>
              {locations.map((loc) => (
                <MenuItem key={loc.ovid} value={String(loc.ovid)}>
                  {loc.location_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Machine Type</InputLabel>
            <Select
              label="Machine Type"
              value={machineTypeFilter}
              onChange={(e) => setMachineTypeFilter(e.target.value)}
            >
              <MenuItem value="">All types</MenuItem>
              <MenuItem value="standard">Standard</MenuItem>
              <MenuItem value="locker">Locker</MenuItem>
              <MenuItem value="drawer">Drawer</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Alert Severity</InputLabel>
            <Select
              label="Alert Severity"
              value={alertSeverityFilter}
              onChange={(e) => setAlertSeverityFilter(e.target.value)}
            >
              <MenuItem value="">Any</MenuItem>
              <MenuItem value="critical">Critical</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="warning">Warning</MenuItem>
              <MenuItem value="info">Info</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={(
              <Checkbox
                checked={needsRestockFilter}
                onChange={(e) => setNeedsRestockFilter(e.target.checked)}
                size="small"
              />
            )}
            label="Needs Restock"
          />
          <FormControlLabel
            control={(
              <Checkbox
                checked={alertsFilter}
                onChange={(e) => setAlertsFilter(e.target.checked)}
                size="small"
              />
            )}
            label="Alerts"
          />
          <FormControlLabel
            control={(
              <Checkbox
                checked={outOfStockFilter}
                onChange={(e) => setOutOfStockFilter(e.target.checked)}
                size="small"
              />
            )}
            label="Out of Stock"
          />
          {hasActiveFilters && (
            <Button variant="text" onClick={clearFilters}>
              Clear Filters
            </Button>
          )}
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6, flexShrink: 0 }}>
            <CircularProgress sx={{ color: COLORS.teal }} />
          </Box>
        )}

        {!loading && error && (
          <Box sx={{ px: { xs: 2, md: 3 }, flexShrink: 0 }}>
            <Alert severity="error" sx={{ mb: 2 }} action={
              <Button color="inherit" size="small" onClick={loadMachines}>Retry</Button>
            }>
              {error}
            </Alert>
          </Box>
        )}

        {!loading && !error && (
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <DataTable
              columns={columns}
              data={rows}
              pageKey="vending-machines"
              idField="id"
              loading={loading}
              onRowClick={(row) => navigate(`/vending/machines/${row.id}`)}
            />
          </Box>
        )}
      </Box>
    </PageContainer>
  );
};

export default VendingMachinesPage;
